import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Flame, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { formatYMDRange } from "@/lib/dates";

function detailPathFor(o) {
  const slug = (o.slug || "").toLowerCase();
  if (slug.includes("solstice-in-the-saddle")) return "/retreats/solstice-in-the-saddle";
  if (slug.includes("winter-solstice-retreat")) return "/retreats/winter-solstice-retreat";
  if (slug.includes("aerial-yoga-teacher-training") && o.type === "training") return "/trainings/aerial-yoga";
  return `/offering/${o.slug}`;
}

const TYPE_LABEL = {
  retreat: "Retreat",
  training: "Teacher Training",
  course: "Online Course",
  event: "Gathering",
};

export default function LastSpotsModule() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/offerings");
        const list = Array.isArray(data) ? data : [];
        // Only date-bound offerings (skip self-paced courses) with real availability.
        // Exclude exclusive-to-studio trainings — they're gated to specific studio
        // instructors via a private link, so they shouldn't appear in a public
        // "last spots remaining" urgency module aimed at general visitors.
        const eligible = list
          .filter((o) => o.start_date && typeof o.spots_left === "number" && o.spots_left > 0 && o.spots_left < 9999 && !o.exclusive_to_studio)
          .sort((a, b) => a.spots_left - b.spots_left || (a.start_date || "").localeCompare(b.start_date || ""))
          .slice(0, 3);
        setItems(eligible);
      } catch (_e) {
        setItems([]);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  if (!loaded || items.length === 0) return null;

  return (
    <section className="py-20 md:py-24 bg-aviana-cream/60 relative overflow-hidden" data-testid="last-spots-module">
      {/* subtle grain */}
      <div className="absolute inset-0 grain pointer-events-none opacity-30" aria-hidden />
      <div className="container-aviana relative">
        {/* header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <p className="overline-plum mb-3 inline-flex items-center gap-2">
              <Flame size={14} className="text-aviana-gold" /> Last spots remaining
            </p>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl text-aviana-ink leading-[1.02]">
              Held by intention — <em className="italic font-serif gold-text">limited by design.</em>
            </h2>
            <p className="mt-5 max-w-xl text-aviana-muted font-serif italic text-[16px] md:text-[18px] leading-[1.55]">
              Each circle is intentionally small. These are the closest doors still open.
            </p>
          </div>
          <Link
            to="/walk-with-me"
            className="text-[11px] tracking-[0.22em] uppercase text-aviana-plum hover:text-aviana-goldDeep inline-flex items-center gap-2 self-start md:self-auto"
            data-testid="last-spots-see-all"
          >
            See every gathering <ArrowUpRight size={14} />
          </Link>
        </div>

        {/* cards */}
        <div className={`grid gap-6 ${items.length === 1 ? "md:grid-cols-1 max-w-xl" : items.length === 2 ? "md:grid-cols-2 max-w-4xl" : "md:grid-cols-3"} ${items.length < 3 ? "mx-auto" : ""}`}>
          {items.map((o) => {
            const path = detailPathFor(o);
            const urgent = o.spots_left <= 3;
            const dateStr = formatYMDRange(o.start_date, o.end_date);
            const handleClick = () => {
              trackEvent("last_spots_card_click", {
                offering_slug: o.slug,
                offering_title: o.title,
                offering_type: o.type,
                spots_left: o.spots_left,
                urgent: urgent,
                price_usd: o.price_usd,
                destination_path: path,
              });
            };
            return (
              <Link
                key={o.id || o.slug}
                to={path}
                onClick={handleClick}
                className="group block bg-aviana-cream rounded-[1.5rem] overflow-hidden border border-aviana-border hover:border-aviana-gold/50 transition-all shadow-[0_2px_20px_-12px_rgba(31,20,36,0.15)] hover:shadow-[0_8px_32px_-12px_rgba(126,63,143,0.25)]"
                data-testid={`last-spots-card-${o.slug}`}
              >
                {/* image with spot badge */}
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={o.image_url}
                    alt={o.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-aviana-ink/55 to-transparent pointer-events-none" />
                  <div className="absolute top-3 left-3">
                    <span className="inline-block px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase text-aviana-cream bg-aviana-plum/85 backdrop-blur-sm rounded-full">
                      {TYPE_LABEL[o.type] || o.type}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase rounded-full font-medium ${urgent ? "bg-aviana-plum text-aviana-cream animate-pulse-soft" : "bg-aviana-gold/90 text-aviana-ink"}`} data-testid={`last-spots-urgency-${o.slug}`}>
                      <Flame size={10} />
                      {o.spots_left === 1 ? "1 spot" : `${o.spots_left} spots`} left
                    </span>
                  </div>
                </div>

                {/* body */}
                <div className="p-6 md:p-7">
                  <h3 className="font-serif text-[22px] md:text-[24px] leading-[1.15] text-aviana-ink group-hover:text-aviana-plum transition-colors line-clamp-2">
                    {o.title}
                  </h3>
                  {o.exclusive_to_studio && (
                    <p className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-aviana-plum font-medium" data-testid={`last-spots-exclusive-${o.slug}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-aviana-plum" />
                      Exclusive · {o.exclusive_studio_name || "Studio instructors only"}
                    </p>
                  )}
                  <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-[12px] text-aviana-muted">
                    {dateStr && (
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar size={12} className="text-aviana-gold" />{dateStr}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={12} className="text-aviana-gold" />{o.location}
                    </span>
                  </div>
                  <div className="mt-5 pt-5 border-t border-aviana-border/70 flex items-end justify-between gap-3">
                    <div>
                      <p className="overline mb-0.5 !text-aviana-muted/70">From</p>
                      <p className="font-figure text-[20px] text-aviana-ink leading-none">${(typeof o.price_usd === "number" ? o.price_usd : 0).toLocaleString()}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] tracking-[0.2em] uppercase text-aviana-plum group-hover:text-aviana-goldDeep group-hover:gap-2 transition-all">
                      Reserve <ArrowUpRight size={13} />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
